/**
 * Rheina Header — sticky morph, dropdowns, and mobile drawer
 */
import { Component } from '@theme/component';

class RheinaHeader extends Component {
  /** @type {ResizeObserver} */
  #resizeObserver = new ResizeObserver(() => {
    if (this.#resizeRaf !== null) return;
    this.#resizeRaf = requestAnimationFrame(() => {
      this.#resizeRaf = null;
      const shell = this.querySelector('.rheina-header__shell');
      if (!(shell instanceof HTMLElement)) return;

      const height = Math.round(shell.getBoundingClientRect().height);
      document.body.style.setProperty('--header-height', `${height}px`);
    });
  });

  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;

  /** @type {((scroll: number) => void) | null} */
  #lenisScrollHandler = null;

  /** @type {number | null} */
  #scrollRaf = null;

  /** @type {number | null} */
  #resizeRaf = null;

  /** @type {number} */
  #lastScrollY = 0;

  /** @type {boolean} */
  #offscreen = false;

  /** @type {boolean} */
  #morphActive = false;

  static #MORPH_ON = 28;
  static #MORPH_OFF = 6;

  #getScrollY() {
    const lenis = window.RheinaLenis?.get?.();
    if (lenis && typeof lenis.scroll === 'number') return lenis.scroll;
    return document.scrollingElement?.scrollTop ?? 0;
  }

  #setMorphState(isActive) {
    const active = isActive ? 'true' : 'false';
    const state = isActive ? 'active' : 'inactive';

    if (
      this.dataset.scrolled === active &&
      this.dataset.stickyActive === active &&
      this.dataset.stickyState === state
    ) {
      return;
    }

    this.dataset.scrolled = active;
    this.dataset.stickyActive = active;
    this.dataset.stickyState = state;
    this.#morphActive = isActive;

    requestAnimationFrame(() => {
      window.RheinaLenis?.resize?.();
    });
  }

  connectedCallback() {
    super.connectedCallback();
    const shell = this.querySelector('.rheina-header__shell');
    if (shell instanceof HTMLElement) this.#resizeObserver.observe(shell);

    this.#initStickyObserver();
    this.#bindLenisScroll();
    document.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#onScroll();

    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#stickyObserver?.disconnect();

    const lenis = window.RheinaLenis?.get?.();
    if (lenis && this.#lenisScrollHandler) {
      lenis.off('scroll', this.#lenisScrollHandler);
    }

    document.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('click', this.#onDocumentClick);

    if (this.#scrollRaf !== null) cancelAnimationFrame(this.#scrollRaf);
    if (this.#resizeRaf !== null) cancelAnimationFrame(this.#resizeRaf);

    document.body.style.setProperty('--header-height', '60px');
  }

  #bindLenisScroll = (attempt = 0) => {
    const lenis = window.RheinaLenis?.get?.();
    if (!lenis) {
      if (attempt < 40) requestAnimationFrame(() => this.#bindLenisScroll(attempt + 1));
      return;
    }

    if (this.#lenisScrollHandler) return;

    this.#lenisScrollHandler = () => this.#onScroll();
    lenis.on('scroll', this.#lenisScrollHandler);
  };

  #initStickyObserver = () => {
    const mode = this.dataset.stickyMode;
    if (!mode || mode === 'never' || mode === 'always') return;

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.#offscreen = !entry.isIntersecting || this.dataset.stickyState === 'active';
      },
      { threshold: 0 }
    );

    this.#stickyObserver.observe(this);
  };

  #onScroll = () => {
    if (this.#scrollRaf !== null) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const mode = this.dataset.stickyMode;
    if (!mode || mode === 'never') return;

    const scrollTop = this.#getScrollY();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollY;
    const isAtTop = headerTop >= 0;

    if (mode === 'always') {
      if (!this.#morphActive && scrollTop > RheinaHeader.#MORPH_ON) {
        this.#setMorphState(true);
      } else if (this.#morphActive && scrollTop < RheinaHeader.#MORPH_OFF) {
        this.#setMorphState(false);
      }

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollY = scrollTop;
      return;
    }

    if (!this.#offscreen) {
      this.#lastScrollY = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        this.#offscreen = false;
        this.#setMorphState(false);
        this.dataset.scrollDirection = 'none';
      } else {
        this.#setMorphState(true);
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
      this.dataset.stickyActive = 'false';
      this.dataset.scrolled = 'false';
      this.#morphActive = false;
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
      this.dataset.stickyActive = 'false';
      this.dataset.scrolled = 'false';
      this.#morphActive = false;
    }

    this.#lastScrollY = scrollTop;
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#closeShopDropdown();
      this.#closeMobileDrawer();
    }
  };

  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;

    const shopTrigger = this.querySelector('[data-shop-trigger]');
    const shopPanel = this.querySelector('[data-shop-panel]');
    if (shopTrigger && shopPanel && !shopTrigger.contains(target) && !shopPanel.contains(target)) {
      this.#closeShopDropdown();
    }
  };

  /** @param {Event} event */
  toggleShopDropdown = (event) => {
    event.preventDefault();
    const trigger = this.querySelector('[data-shop-trigger]');
    const panel = this.querySelector('[data-shop-panel]');
    if (!trigger || !panel) return;

    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      this.#closeShopDropdown();
    } else {
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      this.dataset.shopOpen = 'true';
      requestAnimationFrame(() => {
        panel.dataset.visible = 'true';
      });
    }
  };

  #closeShopDropdown = () => {
    const trigger = this.querySelector('[data-shop-trigger]');
    const panel = this.querySelector('[data-shop-panel]');
    if (!trigger || !panel) return;

    trigger.setAttribute('aria-expanded', 'false');
    panel.dataset.visible = 'false';
    this.dataset.shopOpen = 'false';
    panel.addEventListener(
      'transitionend',
      () => {
        if (trigger.getAttribute('aria-expanded') === 'false') {
          panel.hidden = true;
        }
      },
      { once: true }
    );
  };

  /** @param {Event} event */
  toggleMobileDrawer = (event) => {
    event.preventDefault();
    const drawer = this.querySelector('[data-mobile-drawer]');
    const toggle = this.querySelector('[data-mobile-toggle]');
    if (!drawer || !toggle) return;

    const isOpen = drawer.dataset.open === 'true';
    if (isOpen) {
      this.#closeMobileDrawer();
    } else {
      drawer.dataset.open = 'true';
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('rheina-header--drawer-open');
    }
  };

  #closeMobileDrawer = () => {
    const drawer = this.querySelector('[data-mobile-drawer]');
    const toggle = this.querySelector('[data-mobile-toggle]');
    if (!drawer || !toggle) return;

    drawer.dataset.open = 'false';
    toggle.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('rheina-header--drawer-open');
  };

  /** @param {Event} event */
  closeMobileDrawer = (event) => {
    event.preventDefault();
    this.#closeMobileDrawer();
  };

  /** @param {Event} event */
  openCart = (event) => {
    event.preventDefault();
    this.#closeMobileDrawer();
    if (window.RheinaCart && typeof window.RheinaCart.open === 'function') {
      window.RheinaCart.open();
      return;
    }
    document.dispatchEvent(new CustomEvent('rheina:cart:open'));
  };

  /** @param {Event} event */
  openSearch = (event) => {
    event.preventDefault();
    this.#closeMobileDrawer();
    window.setTimeout(() => {
      const modal = document.querySelector('#search-modal');
      if (modal && typeof modal.showDialog === 'function') {
        modal.showDialog();
      }
    }, 320);
  };
}

if (!customElements.get('rheina-header')) {
  customElements.define('rheina-header', RheinaHeader);
}

// Header group height observer (compatible with theme.liquid)
document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('rheina-header');
  const headerGroup = document.querySelector('#header-group');
  if (!headerGroup) return;

  const updateGroupHeight = () => {
    let total = 0;
    for (const child of headerGroup.children) {
      if (child instanceof HTMLElement) total += child.offsetHeight;
    }
    document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
  };

  let groupResizeRaf = null;
  const scheduleGroupHeightUpdate = () => {
    if (groupResizeRaf !== null) return;
    groupResizeRaf = requestAnimationFrame(() => {
      groupResizeRaf = null;
      updateGroupHeight();
    });
  };

  updateGroupHeight();
  const ro = new ResizeObserver(scheduleGroupHeightUpdate);
  for (const child of headerGroup.children) {
    if (child instanceof HTMLElement) ro.observe(child);
  }

  if (header instanceof HTMLElement) {
    ro.observe(header);
  }
});
